import React, { useState, useEffect, useCallback } from 'react';
import { siteInventoryService } from '../services';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Trash2, Save, MapPin, GripVertical } from 'lucide-react';
import { getApiError } from '@/lib/utils';

export default function SiteLocationsTab({ siteId }) {
  const { toast } = useToast();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const data = await siteInventoryService.listLocations(siteId);
      setLocations(data || []);
      setDirty(false);
    } catch (err) {
      console.error('Failed to load locations:', err);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const addLocation = () => {
    setLocations(prev => [...prev, {
      location_name: '', display_sequence: prev.length, is_active: true, _isNew: true,
    }]);
    setDirty(true);
  };

  const updateLocation = (idx, field, value) => {
    setLocations(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
    setDirty(true);
  };

  const removeLocation = (idx) => {
    setLocations(prev => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = async () => {
    const valid = locations.filter(l => l.location_name.trim());
    if (valid.length === 0 && locations.length > 0) {
      toast({ title: 'Validation', description: 'Location names cannot be empty', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = valid.map((l, idx) => ({
        id: l._isNew ? undefined : l.id,
        location_name: l.location_name.trim(),
        display_sequence: idx,
        is_active: l.is_active,
      }));
      const updated = await siteInventoryService.bulkSaveLocations(siteId, payload);
      setLocations(updated);
      setDirty(false);
      toast({ title: 'Saved', description: 'Locations updated successfully' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="locations-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading locations...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="site-locations-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{locations.length} location{locations.length !== 1 ? 's' : ''}</p>
        <div className="flex gap-2">
          {dirty && (
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-locations-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={addLocation} data-testid="add-location-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add Location
          </Button>
        </div>
      </div>

      {locations.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground" data-testid="locations-empty">
          <MapPin className="h-8 w-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No locations defined</p>
          <p className="text-xs mt-1">Add locations to organise stock counts by area (e.g. Bar, Cellar, Restaurant)</p>
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="w-[30px]"></TableHead>
                <TableHead>Location Name</TableHead>
                <TableHead className="text-center w-[80px]">Active</TableHead>
                <TableHead className="w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((loc, idx) => (
                <TableRow key={loc.id || `new-${idx}`} data-testid={`loc-row-${idx}`}>
                  <TableCell className="text-center text-muted-foreground"><GripVertical className="h-3.5 w-3.5" /></TableCell>
                  <TableCell>
                    <Input className="h-8 text-sm" value={loc.location_name}
                      onChange={(e) => updateLocation(idx, 'location_name', e.target.value)}
                      placeholder="e.g. Main Bar, Cellar, Restaurant"
                      data-testid={`loc-name-${idx}`} />
                  </TableCell>
                  <TableCell className="text-center">
                    <Switch checked={loc.is_active} onCheckedChange={(v) => updateLocation(idx, 'is_active', v)}
                      data-testid={`loc-active-${idx}`} />
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeLocation(idx)}
                      data-testid={`loc-remove-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
